import React, { useState, useCallback, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  EmailRule,
} from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import notify from "devextreme/ui/notify";
import { useAuth } from "../../contexts/auth";
import { removeAllCachedForms } from "../../utils/formCache.js";

import "./login-form.scss";

export default function () {
  const history = useHistory();
  const { signIn, sendToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isRecivedGuid, setIsRecivedGuid] = useState(false);
  const [recivedGuid, setGuid] = useState(null);
  const [formData, setFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const guid = localStorage.getItem("user");

  // Clear form cache when login component mounts
  useEffect(() => {
    removeAllCachedForms();
  }, []);

  const onFormDataChange = useCallback((e) => {
    if (e.name === 'formData') {
      setFormData(e.value);
    }
  }, [setFormData]);

  const onSendEmailAndPw = useCallback(
    async (e) => {
      const { email, password } = formData;
      setLoading(true);

      const result = await signIn(email, password);
      if (result.isOk) {
        setIsRecivedGuid(true);
        setLoading(false);
        setGuid(result.phone);
      } else {
        setLoading(false);
        notify(result.message, "error", 2000);
      }
    },
    [signIn, formData]
  );

  const onSendToken = useCallback(
    async (e) => {
      e.preventDefault();
      const { token } = formData;
      setLoading(true);

      const result = await sendToken(token);
      if (!result.isOk) {
        setLoading(false);
        notify(result.message, "error", 2000);
      } else {
        try {
          const returnTo = sessionStorage.getItem("returnTo");
          if (returnTo) {
            history.push(returnTo);
            sessionStorage.removeItem("returnTo");
          }
        } catch (e) {
          console.error("[onSendToken]:", e);
        }
      }
    },
    [sendToken, formData, history]
  );

  const onCreateAccountClick = useCallback(() => {
    history.push("/create-account");
  }, [history]);

  return (
    <form className={"login-form"} onSubmit={onSendToken}>
      <Form
        formData={formData}
        disabled={loading}
        onOptionChanged={onFormDataChange}
      >
        <Item
          disabled={isRecivedGuid}
          dataField={"email"}
          editorType={"dxTextBox"}
          editorOptions={emailEditorOptions}
        >
          <RequiredRule message="Email is required" />
          <EmailRule message="Email is invalid" />
          <Label visible={false} />
        </Item>
        <Item
          disabled={isRecivedGuid}
          dataField={"password"}
          editorType={"dxTextBox"}
          editorOptions={{
            stylingMode: "filled",
            placeholder: "Password",
            mode: showPassword ? "text" : "password",
            buttons: [
              {
                name: "password",
                location: "after",
                options: {
                  icon: showPassword
                    ? "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'/><line x1='1' y1='1' x2='23' y2='23'/></svg>"
                    : "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/><circle cx='12' cy='12' r='3'/></svg>",
                  type: "default",
                  stylingMode: "text",
                  onClick: () => setShowPassword((prev) => !prev),
                },
              },
            ],
          }}
        >
          <RequiredRule message="Password is required" />
          <Label visible={false} />
        </Item>

        <ButtonItem visible={!isRecivedGuid}>
          <ButtonOptions width={"100%"} onClick={onSendEmailAndPw}>
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width={"24px"} height={"24px"} visible={true} />
              ) : (
                "שלח"
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>

        <Item visible={!isRecivedGuid}>
          <div
            className={"link"}
            style={{ textAlign: "center", marginBottom: "10px" }}
          >
            <Link
              to={{
                pathname: "/reset-password",
                state: { email: formData.email },
              }}
            >
              שכחתי סיסמה
            </Link>
          </div>
        </Item>

        <Item
          visible={isRecivedGuid}
          dataField={"token"}
          editorType={"dxTextBox"}
          editorOptions={tokenEditorOptions}
          helpText={recivedGuid}
        >
          <RequiredRule message="יש להזין את הקוד שנשלח" />
          <Label visible={false} />
        </Item>

        <ButtonItem visible={isRecivedGuid}>
          <ButtonOptions
            width={"100%"}
            type={"default"}
            useSubmitBehavior={true}
          >
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width={"24px"} height={"24px"} visible={true} />
              ) : (
                "כניסה למערכת"
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );
}

const emailEditorOptions = {
  stylingMode: "filled",
  placeholder: "Email",
  mode: "email",
};
const tokenEditorOptions = {
  stylingMode: "filled",
  placeholder: "הזן את הקוד שהתקבל",
  mode: "number",
};
const passwordEditorOptions = {
  stylingMode: "filled",
  placeholder: "Password",
  mode: "password",
};
const rememberMeEditorOptions = {
  text: "Remember me",
  elementAttr: { class: "form-text" },
};